// src/pages/Dashboard.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import * as THREE from 'three';
import NET from 'vanta/dist/vanta.net.min';
import VanillaTilt from 'vanilla-tilt';
import '../styles/dashboard.css';

export default function Dashboard() {
  const navigate    = useNavigate();
  const { section } = useParams();
  const vantaRef    = useRef(null);
  const gridRef     = useRef(null);
  const NUM         = 30;
  const STORAGE_KEY = `slotShuffleGroups_${section}`;

  const [groups,    setGroups]    = useState({ high: [], midHigh: [], midLow: [], low: [] });
  const [modalData, setModalData] = useState(null);
  const [remaining, setRemaining] = useState(0);

  // 1) ตรวจสอบ token + expiresAt และนับถอยหลังตาม expiresAt
  useEffect(() => {
    const token     = localStorage.getItem('token');
    const expiresAt = localStorage.getItem('expiresAt');
    if (!token || !expiresAt) {
      navigate('/login');
      return;
    }

    const expTime = new Date(expiresAt).getTime();
    const tick = () => {
      const nowMs = Date.now();
      const diffMs = expTime - nowMs;
      const secs = Math.max(0, Math.floor(diffMs / 1000));
      setRemaining(secs);
      if (secs <= 0) {
        alert('Session หมดเวลาแล้ว');
        localStorage.removeItem('token');
        localStorage.removeItem('expiresAt');
        navigate('/login');
      }
    };

    tick();
    const iv = setInterval(tick, 5000);
    return () => clearInterval(iv);
  }, [navigate]);

  // helper: แปลงวินาทีเป็น MM:SS
  const formatTime = s => {
    const m   = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = Math.floor(s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('expiresAt');
    navigate('/login');
  };

  // 2) Vanta.js background
  useEffect(() => {
    const effect = NET({
      el: vantaRef.current,
      THREE,
      color: 0x00ffff,
      backgroundColor: 0x000000,
      points: 12.0,
      maxDistance: 25.0,
      spacing: 18.0,
      vertexColors: false
    });
    return () => effect.destroy();
  }, []);

  // 3) Tilt on nav icons
  useEffect(() => {
    const elems = document.querySelectorAll('nav a');
    VanillaTilt.init(elems, { speed: 400, glare: true, "max-glare": 0.3 });
  }, []);

  // 4) Custom cursor
  useEffect(() => {
    const cursor = document.getElementById('cursor');
    const onMove = e => {
      cursor.style.top  = `${e.clientY}px`;
      cursor.style.left = `${e.clientX}px`;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mousedown', () => cursor.style.transform = 'scale(0.7)');
    document.addEventListener('mouseup',   () => cursor.style.transform = 'scale(1)');
    return () => document.removeEventListener('mousemove', onMove);
  }, []);

  // 5) รูปแต่ละ section
  const allImages = {
    pg: [
      '/assets/img/30_game_pg/BakeryBonanza-icon.webp',
      '/assets/img/30_game_pg/CaishenWins-icon.webp',
      '/assets/img/30_game_pg/CandyBonanza-icon.webp',
      '/assets/img/30_game_pg/CandyBurst-icon.webp',
      '/assets/img/30_game_pg/Captain_sBounty-icon.webp',
      '/assets/img/30_game_pg/CashMania-icon.webp',
      '/assets/img/30_game_pg/CryptoGold-icon.webp',
      '/assets/img/30_game_pg/DoubleFortune-icon.webp',
      '/assets/img/30_game_pg/DragonHatch-icon.webp',
      '/assets/img/30_game_pg/DragonLegend-icon.webp',
      '/assets/img/30_game_pg/FortuneGods-icon.webp',
      '/assets/img/30_game_pg/FortuneOx-icon.webp',
      '/assets/img/30_game_pg/Geisha_sRevenge-icon.webp',
      '/assets/img/30_game_pg/GraffitiRush-icon.webp',
      '/assets/img/30_game_pg/GuardiansofIce&Fire-icon.webp',
      '/assets/img/30_game_pg/HawaiianTiki-icon.webp',
      '/assets/img/30_game_pg/HipHopPanda-icon.webp',
      '/assets/img/30_game_pg/IncanWonders-icon.webp',
      '/assets/img/30_game_pg/JungleDelight-icon.webp',
      '/assets/img/30_game_pg/JurassicKingdom-icon.webp',
      '/assets/img/30_game_pg/LegendofPerseus-icon.webp',
      '/assets/img/30_game_pg/Medusa-icon.webp',
      '/assets/img/30_game_pg/Mr.Treasure_sFortune-icon.webp',
      '/assets/img/30_game_pg/PiggyGold-icon.webp',
      '/assets/img/30_game_pg/PinataWins-icon.webp',
      '/assets/img/30_game_pg/QueenofBounty-icon.webp',
      '/assets/img/30_game_pg/SantasGiftRush-icon.webp',
      '/assets/img/30_game_pg/SecretsofCleopatra-icon.webp',
      '/assets/img/30_game_pg/WildHeistCashout-icon.webp',
      '/assets/img/30_game_pg/YakuzaHonor-icon.webp'
    ],
    play: [
      '/assets/img/30_game_pp/1d1dff8c-b13a-4f1e-8ce8-dd8fe4b6010b.webp',
      '/assets/img/30_game_pp/02d9de49-7612-4312-85db-1996cbea0e0a.webp',
      '/assets/img/30_game_pp/4c42f10a-b532-4614-b164-8f768b76d65f.webp',
      '/assets/img/30_game_pp/5ea4d903-63bd-4af8-8cc3-6a8f0882e941.webp',
      '/assets/img/30_game_pp/9c4b10cb-7004-4983-a8ba-dd65b193e017.webp',
      '/assets/img/30_game_pp/9ed73a3f-73e8-47c5-8f93-81fd5a51e0c8.webp',
      '/assets/img/30_game_pp/16dd3599-c63b-4bc1-8450-298fa7231d15.webp',
      '/assets/img/30_game_pp/19eb6ed0-b2b4-42c4-bf56-8352b6d73975.webp',
      '/assets/img/30_game_pp/32f76745-69ec-40b2-9f20-56604eecaa70.webp',
      '/assets/img/30_game_pp/094aaf26-5fb2-471b-9533-c4dc1c2ac62a.webp',
      '/assets/img/30_game_pp/134a0df6-3aae-4eee-9ebc-3e19a541d8fc.webp',
      '/assets/img/30_game_pp/242b17e1-cf15-4031-b18a-445e517bb22b.webp',
      '/assets/img/30_game_pp/387adefe-0002-4ad9-b0a1-ce752381854a.webp',
      '/assets/img/30_game_pp/732bad6b-b65a-4e9e-a090-1b6ae60e052a.webp',
      '/assets/img/30_game_pp/743e19a1-3ef1-42aa-86c8-dbff8147d180.webp',
      '/assets/img/30_game_pp/905c7f5f-e095-46ba-9d88-80b4fab266e0.webp',
      '/assets/img/30_game_pp/3562e9d9-2666-4fb9-8546-7a4989bbf8b8.webp',
      '/assets/img/30_game_pp/6716fb42-c670-4e47-83d1-c303b862c7f6.webp',
      '/assets/img/30_game_pp/08962bd8-9ed1-4605-8944-1962c9dddc4f.webp',
      '/assets/img/30_game_pp/9650b40d-a149-4055-a929-d7ba97f11384.webp',
      '/assets/img/30_game_pp/46121b6a-31be-4a9f-a024-eaaa06660dba.webp',
      '/assets/img/30_game_pp/112631fd-3609-45d5-aef2-b5299e7a31bb.webp',
      '/assets/img/30_game_pp/503652dd-5ee1-4aa3-850c-45ce062bedc7.webp',
      '/assets/img/30_game_pp/903972a1-faf5-4385-899e-1ed4fcb98d7d.webp',
      '/assets/img/30_game_pp/89246965-2d88-44fd-a2dd-af0410c008d1.webp',
      '/assets/img/30_game_pp/b09a2685-375c-4f8e-8173-f38a6e6a9eb6.webp',
      '/assets/img/30_game_pp/c50ebc00-b307-426b-a39a-b57092cd50b1.webp',
      '/assets/img/30_game_pp/caac30f0-51a1-4b2c-ae3b-1023dd67d005.webp',
      '/assets/img/30_game_pp/cb0bbd26-06c4-4095-8e08-337953941737.webp',
      '/assets/img/30_game_pp/e185f692-74de-4696-b986-f18e85b76e5f.webp'
    ],
    jl: [
      '/assets/img/30_game_ji/2da729bc-8147-48cb-8a8f-e63ca7629796.webp',
      '/assets/img/30_game_ji/2fe35d88-d157-4fe8-9677-f947b37e27d2.webp',
      '/assets/img/30_game_ji/03e53f8d-62f5-44db-93b1-44b50fbd7da6.webp',
      '/assets/img/30_game_ji/6ee3fa81-fd94-4c3b-8caf-4b51479ef730.webp',
      '/assets/img/30_game_ji/7a4f813c-376b-4b01-908c-e7ee76b4c057.webp',
      '/assets/img/30_game_ji/8d113828-494b-414e-bf5a-e20c3c50a347.webp',
      '/assets/img/30_game_ji/9b8b5690-7de5-46da-b432-f0422dd3550e.webp',
      '/assets/img/30_game_ji/17db1d1b-a352-446f-994b-72574d274106.webp',
      '/assets/img/30_game_ji/24b41bd7-d99c-4fc7-8e6b-aec9a1546acb.webp',
      '/assets/img/30_game_ji/110a7662-b93d-46e3-8f41-90ada448c6a5.webp',
      '/assets/img/30_game_ji/250e5231-5d24-4708-81c6-0b55e2e5522e.webp',
      '/assets/img/30_game_ji/0532e5c8-b92e-4345-9c8c-2d859c1b6cb3.webp',
      '/assets/img/30_game_ji/7952f4bf-a626-43a9-b43d-695c4a02db3e.webp',
      '/assets/img/30_game_ji/9095d2d2-307c-484d-9801-5be2867800b6.webp',
      '/assets/img/30_game_ji/a7ab05c9-3dac-4997-b35b-82dff4d26ef0.webp',
      '/assets/img/30_game_ji/b1dd5107-9399-450b-acaf-d069e4d36f91.webp',
      '/assets/img/30_game_ji/b63fbaf1-d7f0-4685-87b3-f345e4868aae.webp',
      '/assets/img/30_game_ji/b79ea30e-c06d-480a-b9c8-995e6b10d508.webp',
      '/assets/img/30_game_ji/b6651f8f-52dd-4e74-969e-7c9712112f9d.webp',
      '/assets/img/30_game_ji/c4181438-0b00-404b-92fe-7792efa7301d.webp',
      '/assets/img/30_game_ji/d511df56-e59b-40b9-b802-16bc930e82ec.webp',
      '/assets/img/30_game_ji/e4f148e7-53ba-49eb-ba89-e4742f94294a.webp',
      '/assets/img/30_game_ji/ed11c27f-74a9-4ed2-aa81-75ba782c3239.webp',
      '/assets/img/30_game_ji/fb427f1c-5ef6-4778-8340-9a2b3a95974f.webp',
      '/assets/img/30_game_ji/cfe18963-f3b0-4af8-b0e0-7f1632cfd523.webp',
      '/assets/img/30_game_ji/75b20c4c-001d-4526-895c-4ef84eb7f54e.webp',
      '/assets/img/30_game_ji/87653720-d26a-4e7c-a340-1fee4ea447e3.webp',
      '/assets/img/30_game_ji/4ff2b056-9587-481b-88a1-34ec47ec59c8.webp',
      '/assets/img/30_game_ji/e73838f0-2986-493a-8dbb-65cb72551f9c.webp',
      '/assets/img/30_game_ji/a8dbf75f-c42d-49b2-90e8-4e04c3727013.webp'
    ],
    joker: [
      '/assets/img/30_game_jk/0b967355-05db-4935-889a-5d2d0190b401.webp',
      '/assets/img/30_game_jk/0bb5775b-6496-4cd9-8498-90d58bee8c66.webp',
      '/assets/img/30_game_jk/0d8636f0-60eb-4c2b-86f3-943453059f62.webp',
      '/assets/img/30_game_jk/4fa59564-ee4c-4c72-bab8-3d142d71cd42.webp',
      '/assets/img/30_game_jk/5a6a53d8-5add-447e-8a74-f28d8b17b557.webp',
      '/assets/img/30_game_jk/5db530fb-6b19-4e6e-80b0-a677335d6627.webp',
      '/assets/img/30_game_jk/5ed162e4-98b9-4e67-aa92-d41447a36240.webp',
      '/assets/img/30_game_jk/8d0831e7-1ce5-4c67-b297-65ac1d4eb7e8.webp',
      '/assets/img/30_game_jk/8fa5f789-2e25-48cb-b188-0558a19e993a.webp',
      '/assets/img/30_game_jk/9b3e13ff-16d3-4af5-ba44-476b25a161d9.webp',
      '/assets/img/30_game_jk/14f3064d-f385-412b-849d-9fa1aaa0246d.webp',
      '/assets/img/30_game_jk/23cd7920-59cb-46a2-85f1-39af5c7c0522.webp',
      '/assets/img/30_game_jk/64dd98f5-5cef-4793-a72c-b6876a4dc7bc.webp',
      '/assets/img/30_game_jk/69f18ba6-3aac-4da1-ae72-e848907bc03b.webp',
      '/assets/img/30_game_jk/71e8bd47-1127-4dd8-80b0-bd865f37703f.webp',
      '/assets/img/30_game_jk/81d47b5b-3230-4ad1-8073-af544820abbe.webp',
      '/assets/img/30_game_jk/92b1aa3b-0276-4918-8c3d-1a5257d1a23d.webp',
      '/assets/img/30_game_jk/93e57503-d8e5-4184-8041-6e524dbede15.webp',
      '/assets/img/30_game_jk/700d34dd-510c-4a35-9fdf-7fe4e9d0641d.webp',
      '/assets/img/30_game_jk/8091d6e7-ef7f-489c-812c-ee01ed81dcfb.webp',
      '/assets/img/30_game_jk/42026d19-cc4a-4cd6-ae59-afe9f80dee96.webp',
      '/assets/img/30_game_jk/288006f8-4d87-47e3-979b-588fffd66929.webp',
      '/assets/img/30_game_jk/a4653ed0-dc82-4756-b47f-e833b6d92f0a.webp',
      '/assets/img/30_game_jk/a1791810-06b4-4491-8290-6e78cfff9947.webp',
      '/assets/img/30_game_jk/cd38ee2c-2936-42d5-b39d-3da1e5dcd757.webp',
      '/assets/img/30_game_jk/d04807ec-5c3d-419f-9dac-c36fa525e457.webp',
      '/assets/img/30_game_jk/e6977879-812a-42e1-95ff-e65eb9201bb3.webp',
      '/assets/img/30_game_jk/f5d42cdc-f119-41df-84b1-1920ce59d3c3.webp',
      '/assets/img/30_game_jk/ffc80650-1931-4139-84db-8fe6c8670ef7.webp',
      '/assets/img/30_game_jk/7d86e31d-60c6-4067-9508-8315d34c03d9.webp'
    ],
    fc: [
      '/assets/img/30_game_fc/0f5e8252-755e-4e75-bbc6-86a0426891bd.webp',
      '/assets/img/30_game_fc/1b842fac-b704-428c-8f0a-0333f784cd13.webp',
      '/assets/img/30_game_fc/2f2e8054-f273-4457-93c6-27d11346fe46.webp',
      '/assets/img/30_game_fc/4cfb9482-108c-4f4d-a476-71d4254a7813.webp',
      '/assets/img/30_game_fc/4dd618e7-8cdb-4e04-9933-ba666b9556b5.webp',
      '/assets/img/30_game_fc/5f4126db-34f5-4fe9-8456-6d56170e5594.webp',
      '/assets/img/30_game_fc/6d037420-8095-4108-8a4d-389ced6ead85.webp',
      '/assets/img/30_game_fc/7af66bcf-964f-42d8-986b-144bfbfa6016.webp',
      '/assets/img/30_game_fc/8e98b8db-88ca-4519-92e3-1f8eb1307efa.webp',
      '/assets/img/30_game_fc/9a22b6ac-0c53-48ef-971b-55a9fb783a3a.webp',
      '/assets/img/30_game_fc/9bb82979-c1fa-413e-8fd0-1f62ea1974d5.webp',
      '/assets/img/30_game_fc/51a05af2-b7ee-42b2-a8bc-9a42190435b5.webp',
      '/assets/img/30_game_fc/77f4caee-f8cf-4955-aff3-369cce781097.webp',
      '/assets/img/30_game_fc/349d5c5e-c033-4ac6-9c41-85c4d9c929db.webp',
      '/assets/img/30_game_fc/6571d7f6-4029-482a-8f9c-ab563a49cae8.webp',
      '/assets/img/30_game_fc/6767f74d-368c-4e8e-bc09-e3a689b3367b.webp',
      '/assets/img/30_game_fc/37356e1a-896d-40fe-a5c0-dca69fc5f232.webp',
      '/assets/img/30_game_fc/246533d5-491d-487e-8689-7e51781eb0d2.webp',
      '/assets/img/30_game_fc/ace2a95a-820d-4552-a5a1-180813e82d0c.webp',
      '/assets/img/30_game_fc/b5e5bf02-9ca0-47b4-9427-47c471511a6c.webp',
      '/assets/img/30_game_fc/b6d100a4-a59b-4f60-a027-18e74e94b6ac.webp',
      '/assets/img/30_game_fc/b9a54c51-c992-4cdc-897c-69e71b5c4f1f.webp',
      '/assets/img/30_game_fc/b65f9335-cfe5-4362-9e69-0e0093e02b2a.webp',
      '/assets/img/30_game_fc/c1a38b5d-19ab-454e-9504-1c0538aedb33.webp',
      '/assets/img/30_game_fc/ce4cccf4-b2d4-42d0-b63f-1cf91e158364.webp',
      '/assets/img/30_game_fc/e0e62625-6ad0-4d33-8458-3cd6b9fa09b3.webp',
      '/assets/img/30_game_fc/e786d71f-04c8-4944-836e-a0b5d7c5c5a3.webp',
      '/assets/img/30_game_fc/fcff2d7a-7c8b-4834-8f1e-22c7fb645833.webp',
      '/assets/img/30_game_fc/fdbd7f97-c1d6-4f96-842e-4dda19b85aa5.webp',
      '/assets/img/30_game_fc/9acd223e-c459-483a-b8bd-c1adfcdcf8a4.webp'
    ]
  };

  // 6) เลือกรูปตาม section
  const imageList = allImages[section] || allImages.pg;

  // 7) สุ่มแบ่งกลุ่ม & cache 1 ชั่วโมง เมื่อ section เปลี่ยน
  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    let obj   = raw ? JSON.parse(raw) : null;
    if (!obj || Date.now() > obj.expires) {
      const pick = n => {
        const arr = Array.from({ length: NUM }, (_, i) => i + 1);
        const out = [];
        for (let i = 0; i < n; i++) {
          const idx = Math.floor(Math.random() * arr.length);
          out.push(arr.splice(idx, 1)[0]);
        }
        return out;
      };
      obj = {
        high:    pick(6),
        midHigh: pick(10),
        midLow:  pick(4),
        low:     pick(10),
        expires: Date.now() + 3600_000
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    }
    setGroups(obj);
  }, [section, STORAGE_KEY]);

  // 8) สร้าง grid & อัปเดต stat ทุก 5 วิ
  useEffect(() => {
    const grid = gridRef.current;
    grid.innerHTML = '';
    imageList.forEach((src, i) => {
      const card = document.createElement('div');
      card.className     = 'feature-card';
      card.dataset.index = i + 1;
      card.innerHTML = `
        <img src="${src}" alt="Game ${i+1}" />
        <div class="players">–</div>
        <div class="stat">Win rate <span class="percent">–.–%</span>
          <div class="bar-container"><div class="bar-fill"></div></div>
        </div>
        <div class="stat">Scatter <span class="percent">–.–%</span>
          <div class="bar-container"><div class="bar-fill"></div></div>
        </div>
        <div class="stat">Wild <span class="percent">–.–%</span>
          <div class="bar-container"><div class="bar-fill"></div></div>
        </div>
      `;
      card.addEventListener('click', () => setModalData({ card }));
      grid.appendChild(card);
    });

    const randomBetween = (min, max) => Math.random() * (max - min) + min;
    const toPercent     = v => v.toFixed(2) + '%';
    const getColor      = p => `hsl(${(p/100)*120},100%,50%)`;

    function updateAll() {
      document.querySelectorAll('.feature-card').forEach((card, idx) => {
        const num  = idx + 1;
        const base = Math.round(randomBetween(10000, 20000));
        card.querySelector('.players').textContent = `ဆော့သူအရေအတွက် ${base.toLocaleString()}`;
        let win;
        if      (groups.high.includes(num))    win = randomBetween(80,99);
        else if (groups.midHigh.includes(num)) win = randomBetween(60,80);
        else if (groups.midLow.includes(num))  win = randomBetween(40,60);
        else                                   win = randomBetween(20,40);

        const percents = card.querySelectorAll('.percent');
        const bars     = card.querySelectorAll('.bar-fill');
        [win, win, win].forEach((v,j) => {
          percents[j].textContent       = toPercent(v);
          percents[j].style.color       = getColor(v);
          bars[j].style.width           = v + '%';
          bars[j].style.backgroundColor = getColor(v);
        });

        card.querySelector('img').classList.toggle('faded', win < 40);
        card.classList.toggle('high-box', groups.high.includes(num));
      });
    }

    updateAll();
    const timer = setInterval(updateAll, 5000);
    return () => clearInterval(timer);
  }, [groups, imageList]);

  // 9) Modal handlers
  const closeModal  = () => setModalData(null);
  const openContact = () => window.open('https://t.me/MM159bot','_blank');

  return (
    <div className="dashboard">
      {/* Logout */}
      <button
        onClick={handleLogout}
        style={{
          position:    'fixed',
          top:         '20px',
          right:       '20px',
          padding:     '8px 16px',
          background:  '#ff3860',
          color:       '#fff',
          border:      'none',
          borderRadius:'4px',
          cursor:      'pointer',
          zIndex:      4001
        }}
      >
        Logout
      </button>

      {/* Session timer */}
      <div className="session-timer">
        အချိန်ကျန်နေသေးပါတယ်: {formatTime(remaining)}
      </div>

      {/* Vanta background */}
      <div ref={vantaRef} id="vantabg" />
      <div className="overlay scanlines" />
      <div className="overlay flicker" />
      <div id="cursor" />

      {/* Header & nav */}
      <header>
        <div className="header-left">
          <img src="/assets/img/mm159_logo.png" alt="Site Logo" className="site-logo" />
          <h1 className="site-title">
            <span data-text="AI Scan အနိုင်များဆုံးဂိမ်း">AI Scan အနိုင်များဆုံးဂိမ်း</span>
          </h1>
          <nav>
            {['pg','play','jl','joker','fc'].map(id => (
              <Link key={id} to={`/dashboard/${id}`} title={id.toUpperCase()}>
                <img src={`/assets/img/logo/${id}.png`} alt={id.toUpperCase()} />
              </Link>
            ))}
          </nav>
        </div>
      </header>

      {/* Social bar */}
      <div className="social-bar">
        {[
          ['https://line.me/R/ti/p/@216cnwno','line','imgimage-stickyline-result.webp'],
          ['https://www.facebook.com/share/16MDmo7WKs','fb','imgfacebook-sticky-result.webp'],
          ['https://t.me/MM159bot','tg','imgtelegram-sticky-result.webp']
        ].map(([link,cls,img]) => (
          <a key={cls} href={link} className={`social-btn ${cls}`} target="_blank" rel="noreferrer">
            <img src={`/assets/img/${img}`} alt={cls} />
          </a>
        ))}
      </div>

      {/* Main grid */}
      <div className="container">
        <div className="rank-header">
          <h2>အခုရေပန်းစားနေတဲ့ဂိမ်း 30မျိုး</h2>
        </div>
        <div id="grid" ref={gridRef} className="features-grid" />
      </div>

      {/* Modal */}
      {modalData && (
        <div id="game-modal" className="show" onClick={e => e.target.id==='game-modal' && closeModal()}>
          <div className="modal-container">
            <span className="modal-close" onClick={closeModal}>&times;</span>
            <img className="modal-img" src={modalData.card.querySelector('img').src} alt="" />
            <div className="modal-stats">
              <p> <span>{modalData.card.querySelector('.players').textContent}</span></p>
              <p>Win rate <span>{modalData.card.querySelectorAll('.percent')[0].textContent}</span></p>
              <div className="bar-container">
                <div className="bar-fill" style={{
                  width:          modalData.card.querySelectorAll('.bar-fill')[0].style.width,
                  backgroundColor:modalData.card.querySelectorAll('.bar-fill')[0].style.backgroundColor
                }} />
              </div>
              <p>Scatter <span>{modalData.card.querySelectorAll('.percent')[1].textContent}</span></p>
              <div className="bar-container">
                <div className="bar-fill" style={{
                  width:          modalData.card.querySelectorAll('.bar-fill')[1].style.width,
                  backgroundColor:modalData.card.querySelectorAll('.bar-fill')[1].style.backgroundColor
                }} />
              </div>
              <p>Wild <span>{modalData.card.querySelectorAll('.percent')[2].textContent}</span></p>
              <div className="bar-container">
                <div className="bar-fill" style={{
                  width:          modalData.card.querySelectorAll('.bar-fill')[2].style.width,
                  backgroundColor:modalData.card.querySelectorAll('.bar-fill')[2].style.backgroundColor
                }} />
              </div>
              <button className="modal-play" onClick={openContact}>ဂိမ်းဝင်ဆော့ရန်</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
